import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The creative session's chat assistant — ONE implementation, two screens.
 *
 * WAVE 4. v2 had no chat panel at all; this is the behaviour half of putting
 * one there, and v1's panel is rebound to it so both get the fixes.
 *
 * WHAT THE SWEEP FOUND:
 *
 *   1. **A FAILED SEND WAS PUSHED INTO THE TRANSCRIPT AS AN ASSISTANT REPLY.**
 *      `role: 'assistant', content: 'Sorry, something went wrong. Please try
 *      again.'` — rendered in the assistant's bubble, in the assistant's voice,
 *      indistinguishable from a real answer the model gave. That is rule 10 in
 *      the place it does the most damage: an operator can act on advice the
 *      model never gave. A failure is now its own kind of row, and the message
 *      the operator typed is handed BACK so it is not lost.
 *   2. `loadPreviousMessages` swallowed its error, so a failed history read
 *      left an empty panel — identical to a session with no history.
 *   3. The transcript save (`/end`) swallowed too, on both the explicit call
 *      and the unmount path. A transcript that never saved looked like one that
 *      did.
 */
export function useCreativeChat({ sessionId, currentStep, getStepContent }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  const [historyState, setHistoryState] = useState('idle');
  const [historyError, setHistoryError] = useState(null);
  const [transcriptNote, setTranscriptNote] = useState(null);

  const savedRef = useRef(false);

  const loadHistory = useCallback(async () => {
    if (!sessionId) return;
    setHistoryState('loading');
    try {
      const res = await axios.get(`${API}/creative/sessions/${sessionId}/messages`);
      setMessages(res.data.messages || []);
      setHistoryState('ok');
      setHistoryError(null);
    } catch (error) {
      setHistoryState('failed');
      setHistoryError(errorText(error));
    }
  }, [sessionId]);

  /**
   * Save the transcript.
   *
   * Reports its own failure. The unmount path below deliberately does NOT — a
   * component being torn down has nowhere to show a message, and inventing one
   * would be worse than the silence. What it does instead is not mark the
   * transcript saved, so the next explicit save still runs.
   */
  const saveTranscript = async () => {
    if (!sessionId || messages.length === 0) return false;
    try {
      await axios.post(`${API}/creative/sessions/${sessionId}/end`);
      savedRef.current = true;
      setTranscriptNote('Transcript saved.');
      return true;
    } catch (error) {
      setTranscriptNote(`The transcript could not be saved: ${errorText(error)}`);
      return false;
    }
  };

  useEffect(() => () => {
    if (sessionId && !savedRef.current) {
      axios.post(`${API}/creative/sessions/${sessionId}/end`).catch(() => {});
    }
  }, [sessionId]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending || !sessionId) return false;
    setInput('');
    setSending(true);

    const tempId = `temp_${Date.now()}`;
    setMessages((prev) => [...prev, {
      message_id: tempId, role: 'user', content: text, step: currentStep,
    }]);

    try {
      const res = await axios.post(`${API}/creative/sessions/${sessionId}/chat`, {
        message: text,
        step: currentStep,
        step_content: getStepContent ? getStepContent() : {},
      });
      // A 200 IS NOT AUTOMATICALLY AN ANSWER. A response missing either message
      // used to be appended as `undefined` and took the whole panel down on the
      // next render — the conversation with it. Caught by test: the malformed
      // shape is reported as a failure, which is what it is.
      const { user_message: um, assistant_message: am } = res.data || {};
      if (!um || !am) {
        setMessages((prev) => [
          ...prev.filter((m) => m.message_id !== tempId),
          { message_id: tempId, role: 'user', content: text, step: currentStep },
          { message_id: `fail_${Date.now()}`, role: 'failure', step: currentStep,
            content: 'the assistant answered, but the reply was not in a shape '
                   + 'this screen can read' },
        ]);
        setInput(text);
        return false;
      }
      setMessages((prev) => [
        ...prev.filter((m) => m.message_id !== tempId), um, am,
      ]);
      return true;
    } catch (error) {
      // A FAILURE, NOT A REPLY. `role: 'failure'` is not a role the backend
      // ever returns, so it cannot be confused with one, and the screen renders
      // it as an error. The operator's own words are handed back rather than
      // being swallowed with the attempt.
      setMessages((prev) => [
        ...prev.filter((m) => m.message_id !== tempId),
        { message_id: tempId, role: 'user', content: text, step: currentStep },
        { message_id: `fail_${Date.now()}`, role: 'failure',
          content: errorText(error), step: currentStep },
      ]);
      setInput(text);
      return false;
    } finally {
      setSending(false);
    }
  };

  return {
    messages, input, setInput, sending, send,
    historyState, historyError, loadHistory,
    transcriptNote, saveTranscript,
  };
}
